/**
 * ContractsTab — Lista de contratos con CRUD, filtro por empleado, modal crear/editar.
 */
import { useState } from 'react'
import { Plus, Search, Pencil, FileSignature, AlertTriangle, Eye, FileText, ExternalLink } from 'lucide-react'
import { useContracts, useCreateContract, useUpdateContract, useEmployees, type EmploymentContract } from '../hooks/usePayroll'
import { usePermission } from '../../../hooks/usePermission'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import { extractErrorMessage } from '../../../lib/errorUtils'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import ContractModal from './ContractModal'
import ContractDetailModal from './ContractDetailModal'

const TYPE_BADGE: Record<string, { label: string; cls: string }> = {
  indefinido: { label: 'Indefinido', cls: 'bg-success-surface-strong text-success-content' },
  plazo_fijo: { label: 'Plazo Fijo', cls: 'bg-warning-surface-strong text-warning-content' },
  obra:       { label: 'Por Obra', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
}

export default function ContractsTab() {
  const [search, setSearch] = useState('')
  const [currentOnly, setCurrentOnly] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const [viewing, setViewing] = useState<EmploymentContract | null>(null)
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()

  const { data: empData } = useEmployees({ active: 'true', page_size: 9999 })
  const { data, isLoading } = useContracts({
    search: search || undefined,
    current: currentOnly ? 'true' : undefined,
  })
  const { mutate: create, isPending: isCreating } = useCreateContract()
  const { mutate: update, isPending: isUpdating } = useUpdateContract(editing?.id || '')

  const contracts = data?.contracts || []
  const employees = empData?.employees || []

  const handleSubmit = (formData: any) => {
    const mutation = editing?.id ? update : create
    mutation(formData, {
      onSuccess: () => {
        toast.success(editing?.id ? 'Contrato actualizado' : 'Contrato creado')
        setModalOpen(false)
        setEditing(null)
      },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al guardar contrato')),
    })
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar nombre o RUT" type="text" placeholder="Buscar nombre o RUT..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
          <input type="checkbox" checked={currentOnly} onChange={e => setCurrentOnly(e.target.checked)}
            className="rounded border-divider-strong" />
          Solo vigentes
        </label>
        {can('payroll.contracts', 'create') && (
          <button onClick={() => { setEditing(null); setModalOpen(true) }}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={14} /> Nuevo contrato
          </button>
        )}
      </div>

      <DataTable<EmploymentContract>
        columns={[
          { key: 'employee_name', header: 'Empleado', sortable: true,
            render: c => <span className="font-medium text-content">{c.employee_name}</span> },
          { key: 'contract_type', header: 'Tipo', sortable: true,
            sortValue: c => (TYPE_BADGE[c.contract_type] || TYPE_BADGE.indefinido).label,
            render: c => {
              const typeBadge = TYPE_BADGE[c.contract_type] || TYPE_BADGE.indefinido
              // El plazo fijo que vence dentro de 30 días lleva su aviso acá:
              // es el dato por el que se entra a esta pantalla.
              const isExpiring = c.contract_type === 'plazo_fijo' && c.end_date &&
                new Date(c.end_date) <= new Date(Date.now() + 30 * 86400000)
              return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${typeBadge.cls}`}>
                  {typeBadge.label}
                  {isExpiring && <AlertTriangle size={10} className="text-warning-solid" />}
                </span>
              )
            } },
          { key: 'position', header: 'Cargo', sortable: true,
            render: c => <span className="text-content-muted">{c.position || '—'}</span> },
          { key: 'department', header: 'Departamento', sortable: true,
            render: c => <span className="text-content-muted">{c.department || '—'}</span> },
          { key: 'base_salary', header: 'Sueldo Base', numeric: true, sortable: true,
            sortValue: c => Number(c.base_salary),
            render: c => (
              <span className="font-medium text-primary">
                ${parseFloat(c.base_salary).toLocaleString('es-CL')}
              </span>
            ) },
          { key: 'start_date', header: 'Inicio', sortable: true,
            render: c => <span className="text-content-muted">{formatDate(c.start_date, locale)}</span> },
          { key: 'end_date', header: 'Término', sortable: true,
            render: c => (
              <span className="text-content-muted">
                {c.end_date ? formatDate(c.end_date, locale) : <span className="text-content-subtle">Indefinido</span>}
              </span>
            ) },
          { key: 'work_hours_weekly', header: 'Jornada', numeric: true, sortable: true,
            sortValue: c => Number(c.work_hours_weekly),
            render: c => <span className="text-content-muted">{c.work_hours_weekly}h/sem</span> },
          { key: 'documento', header: 'Doc.', width: 64,
            render: c => (((c as any).document_file_url || (c as any).document_url) ? (
              <span className="inline-flex items-center gap-1 text-xs text-success-content-soft">
                {(c as any).document_file_url ? <FileText size={14} /> : <ExternalLink size={14} />}
              </span>
            ) : (
              <span className="text-xs text-content-subtle">—</span>
            )) },
          { key: 'acciones', header: 'Acciones', width: 88,
            render: c => (
              <div className="flex items-center justify-center gap-1" onClick={e => e.stopPropagation()}>
                <button onClick={() => setViewing(c)} title="Ver detalle"
                  className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                  <Eye size={14} />
                </button>
                {can('payroll.contracts', 'edit') && c.is_current && (
                  <button onClick={() => { setEditing(c); setModalOpen(true) }} title="Editar"
                    className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            ) },
        ]}
        rows={contracts}
        rowKey={c => c.id}
        minWidth={1180}
        loading={isLoading}
        onRowClick={c => setViewing(c)}
        // Un contrato que ya no rige se atenúa entero: es lo único que no se
        // puede resolver celda por celda, porque el fondo y los bordes de la
        // fila quedarían sin atenuar.
        rowClassName={c => (!c.is_current ? 'opacity-50' : undefined)}
        empty={<EmptyState title="Sin contratos registrados." size="sm" />}
        caption="Contratos de trabajo vigentes e históricos"
      />

      <ContractModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditing(null) }}
        onSubmit={handleSubmit}
        isPending={isCreating || isUpdating}
        initial={editing}
        employees={employees}
      />
      <ContractDetailModal
        contract={viewing}
        onClose={() => setViewing(null)}
        onEdit={(c) => { setViewing(null); setEditing(c); setModalOpen(true) }}
      />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}
